import { useState, useEffect } from 'react';
// v2026.07.21.force_deploy_full_russian_localization
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import toast from 'react-hot-toast';
import { useConfirmStore } from '../store/confirmStore';
import { ArrowLeft, Edit, Plus, Trash2, Server, Zap, Weight, ShieldCheck, Activity, RefreshCw, Cable as CableIcon } from 'lucide-react';
import { DeviceFaceplateViewer } from '../components/equipment/DeviceFaceplateViewer';
import { RackPositionMiniMap } from '../components/equipment/RackPositionMiniMap';
import { DeviceEditModal } from '../components/equipment/DeviceEditModal';
import { DeviceSwapModal } from '../components/equipment/DeviceSwapModal';
import { TYPE_NAMES_RU } from '../components/canvas/equipmentTypes';
import { rackLabel } from '../utils/rackName.logic';

export default function DeviceDetailsPage() {
  const { id, dcCode } = useParams<{ id: string; dcCode: string }>();
  const [searchParams] = useSearchParams();
  const fromRack = searchParams.get('fromRack');

  const [device, setDevice] = useState<any | null>(null);
  const [cables, setCables] = useState<any[]>([]);
  const [rack, setRack] = useState<any | null>(null);
  const [rackEquipment, setRackEquipment] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [showAddPort, setShowAddPort] = useState(false);
  const [newPort, setNewPort] = useState({ name: '', port_type: 'network', connector_type: 'RJ45' });
  const [portNotes, setPortNotes] = useState<Record<string, string>>({});

  const fetchDeviceData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const devRes = await api.get(`/api/equipment/${id}`);
      const devData = devRes.data;
      setDevice(devData);

      try {
        const cablesRes = await api.get(`/api/equipment/${id}/cables`);
        setCables(cablesRes.data || []);
      } catch (e) {
        console.warn('Cables fetch failed:', e);
      }

      if (devData.rack_id) {
        try {
          const rackRes = await api.get(`/api/datacenters/racks/${devData.rack_id}`);
          setRack(rackRes.data);

          if (rackRes.data?.datacenter_code) {
            const eqRes = await api.get(`/api/datacenters/${rackRes.data.datacenter_code}/racks/${devData.rack_id}/equipment`);
            setRackEquipment(eqRes.data || []);
          }
        } catch (e) {
          console.warn('Rack context fetch failed:', e);
        }
      }

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeviceData();
  }, [id]);

  const handleAddPort = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/api/equipment/${id}/ports`, newPort);
      setShowAddPort(false);
      fetchDeviceData();
      setNewPort({ name: '', port_type: 'network', connector_type: 'RJ45' });
      toast.success('Порт успешно добавлен');
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Неизвестная ошибка';
      toast.error(`Не удалось добавить порт: ${msg}`);
      console.error(err);
    }
  };

  const handleDeletePort = async (portId: string, portName: string) => {
    const confirmed = await useConfirmStore.getState().confirm({
      title: 'Удаление порта',
      message: `Удалить порт ${portName}?`,
      confirmText: 'Удалить',
      cancelText: 'Отмена'
    });
    if (!confirmed) return;
    try {
      await api.delete(`/api/equipment/ports/${portId}`);
      fetchDeviceData();
      toast.success('Порт удален');
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Неизвестная ошибка';
      toast.error(`Не удалось удалить порт: ${msg}`);
      console.error(err);
    }
  };

  const handlePortStatusChange = async (portId: string, newStatus: string, notes?: string) => {
    try {
      await api.patch(`/api/equipment/ports/${portId}`, { status: newStatus, notes: notes ?? portNotes[portId] ?? null });
      fetchDeviceData();
      toast.success('Статус порта обновлен');
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Ошибка обновления порта';
      toast.error(`Не удалось изменить статус порта: ${msg}`);
      console.error('Port status update failed', err);
    }
  };

  const handlePortNotesSave = async (portId: string) => {
    try {
      await api.patch(`/api/equipment/ports/${portId}`, { notes: portNotes[portId] ?? '' });
      toast.success('Заметка порта сохранена');
    } catch (err: any) {
      console.error('Notes save failed', err);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400 font-mono">Загрузка спецификаций оборудования...</div>;
  if (!device) return <div className="p-8 text-center text-rose-500 font-mono">Оборудование не найдено</div>;

  const returnRackPath = rack?.datacenter_code 
    ? `/${rack.datacenter_code}/racks/${device.rack_id}`
    : `/racks/${device.rack_id}`;
  
  const backPath = fromRack
    ? returnRackPath
    : rack?.datacenter_code
      ? `/${rack.datacenter_code}/equipment`
      : '/equipment';

  const rackTitle = rack ? rackLabel({ ...rack, address_confirmed: rack.address_confirmed === true }) : `Стойка #${device.rack_id}`;
  const unconfirmed = rack && rack.address_confirmed !== true && !rack.is_facility && !['pillar', 'column'].includes(rack.type);

  return (
    <div className="h-full flex flex-col bg-slate-950 text-slate-100 font-sans overflow-y-auto custom-scrollbar">
      
      {/* Header Banner */}
      <div className="px-6 py-4 border-b border-slate-800/20 bg-slate-900/40 backdrop-blur-md flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <Link 
            to={backPath} 
            className="p-2 bg-slate-900/40 border border-slate-800/50 hover:bg-slate-800/25 rounded-lg text-slate-300 hover:text-slate-100 transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
            title="Назад"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl font-bold font-mono text-slate-100 tracking-wide">
                {device.name}
              </h1>
              <span className="px-2 py-0.5 text-xs font-mono rounded bg-green-500/10 border border-green-500/30 text-green-400">
                АКТИВЕН
              </span>
              <span className="px-2 py-0.5 text-xs font-mono rounded bg-slate-900/60 border border-slate-800/50 text-accent">
                {device.equipment_type === 'server' ? 'СЕРВЕР' :
                 device.equipment_type === 'switch' ? 'КОММУТАТОР' :
                 device.equipment_type === 'router' ? 'МАРШРУТИЗАТОР' :
                 device.equipment_type === 'patch_panel' ? 'ПАТЧ-ПАНЕЛЬ' :
                 device.equipment_type === 'storage' ? 'СХД' :
                 device.equipment_type === 'firewall' ? 'ФАЙРВОЛ' :
                 device.equipment_type === 'pdu' ? 'PDU' :
                 device.equipment_type === 'ups' ? 'ИБП' : device.equipment_type.toUpperCase()}
              </span>
            </div>
            <p className="text-xs text-slate-400 font-mono mt-0.5">
              Профиль Оборудования // ID: {device.id}
            </p>
          </div>
        </div>

        {/* Header Actions */}
        <div className="flex flex-wrap gap-2.5 justify-end">
          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-cyan-950/20 border border-cyan-800/50 text-cyan-400 rounded-lg hover:bg-cyan-900/30 transition-colors duration-200 text-xs font-mono font-medium shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
          >
            <Edit className="w-4 h-4" /> Редактировать прибор
          </button>

          <button
            onClick={() => setShowSwapModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-rose-950/20 border border-rose-900/50 text-rose-400 rounded-lg hover:bg-rose-900/30 transition-colors duration-200 text-xs font-mono font-medium shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
          >
            <RefreshCw className="w-4 h-4 text-rose-400" /> Заменить (Impact Analysis)
          </button>
          
          {device.rack_id && (
            <Link 
              to={returnRackPath} 
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-900/40 border border-slate-800/50 text-slate-300 hover:text-slate-100 rounded-lg hover:bg-slate-800/25 transition-colors duration-200 text-xs font-mono font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
            >
              <Server className="w-4 h-4 text-cyan-400" /> Перейти к Стойке
            </Link>
          )}

          <Link 
            to={rack?.datacenter_code ? `/${rack.datacenter_code}/topology` : '#'} 
            className="flex items-center gap-1.5 px-3 py-2 bg-indigo-950/20 border border-indigo-800/50 text-indigo-400 rounded-lg hover:bg-indigo-900/30 transition-colors duration-200 text-xs font-mono font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
          >
            <Activity className="w-4 h-4" /> Топология
          </Link>
        </div>
      </div>

      {/* Main Grid Content */}
      <div className="flex-1 p-6 space-y-6 max-w-7xl mx-auto w-full">
        
        {/* Top Grid: Specs + Rack MiniMap */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Card 1: Specifications */}
          <div className="md:col-span-2 bg-slate-900 border border-slate-800/50 rounded-xl p-5 shadow-sm flex flex-col gap-4">
            <div className="flex justify-between items-center border-b border-slate-800/20 pb-3">
              <h2 className="text-sm font-mono font-bold uppercase text-slate-100 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-cyan-400" /> Спецификации и Атрибуты OOUX
              </h2>
              <span className="text-[10px] font-mono text-slate-400">Синхронизация NetBox: {device.netbox_id ? `#${device.netbox_id}` : 'Локально'}</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs font-mono">
              <div className="p-3 bg-slate-950/40 border border-slate-800/30 rounded-lg">
                <span className="text-[10px] text-slate-400 uppercase block mb-0.5">Тип устройства</span>
                <span className="font-bold text-slate-100">
                  {TYPE_NAMES_RU[device.equipment_type] || device.equipment_type}
                </span>
              </div>

              <div className="p-3 bg-slate-950/40 border border-slate-800/30 rounded-lg">
                <span className="text-[10px] text-slate-400 uppercase block mb-0.5">Монтажный Размер</span>
                <span className="font-bold text-cyan-400">{device.u_height} U</span>
              </div>

              <div className="p-3 bg-slate-950/40 border border-slate-800/30 rounded-lg">
                <span className="text-[10px] text-slate-400 uppercase block mb-0.5">U-Позиция</span>
                <span className="font-bold text-cyan-400">Слот {device.u_position}</span>
              </div>

              <div className="p-3 bg-slate-950/40 border border-slate-800/30 rounded-lg">
                <span className="text-[10px] text-slate-400 uppercase block mb-0.5">Ориентация</span>
                <span className="font-bold text-slate-100">
                  {device.mounting_position?.toLowerCase() === 'front' ? 'Фронтальная' :
                   device.mounting_position?.toLowerCase() === 'rear' ? 'Тыльная' :
                   device.mounting_position?.toLowerCase() === 'full depth' ? 'На всю глубину' :
                   device.mounting_position || 'Фронтальная'}
                </span>
              </div>

              <div className="p-3 bg-slate-950/40 border border-slate-800/30 rounded-lg">
                <span className="text-[10px] text-slate-400 uppercase block mb-0.5 flex items-center gap-1">
                  <Zap className="w-3 h-3 text-amber-500" /> Энергопотребление
                </span>
                <span className="font-bold text-text-primary">{device.power_w == null ? 'Неизвестно' : `${device.power_w} Вт`}</span>
              </div>

              <div className="p-3 bg-slate-950/40 border border-slate-800/30 rounded-lg">
                <span className="text-[10px] text-slate-400 uppercase block mb-0.5 flex items-center gap-1">
                  <Weight className="w-3 h-3 text-cyan-400" /> Вес
                </span>
                <span className="font-bold text-text-primary">{device.weight_kg == null ? 'Неизвестно' : `${device.weight_kg} кг`}</span>
              </div>
            </div>

            <div className="p-3 bg-slate-950/40 border border-slate-800/30 rounded-lg text-xs font-mono flex items-center justify-between">
              <span className="text-slate-300">Стойка назначения:</span>
              {device.rack_id ? (
                <Link 
                  to={returnRackPath} 
                  className="font-bold text-cyan-400 hover:text-cyan-300 hover:underline flex items-center gap-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
                >
                  {rackTitle}
                  <Server className="w-3.5 h-3.5 text-cyan-400 ml-1" />
                </Link>
              ) : (
                <span className="font-bold text-slate-400">Не смонтировано (Zero U)</span>
              )}
            </div>
            {unconfirmed && <p className="text-xs text-[var(--text-secondary)]">Место не подтверждено</p>}
          </div>

          {/* Card 2: Rack Location MiniMap */}
          <div className="md:col-span-1">
            {Number.isInteger(rack?.u_slots) && rack.u_slots > 0 ? <RackPositionMiniMap
              totalU={rack.u_slots}
              uPosition={device.u_position}
              uHeight={device.u_height}
              equipmentName={device.name}
              rackName={rackTitle}
            /> : <p className="text-xs text-[var(--text-secondary)]">Вместимость стойки не указана</p>}
          </div>

        </div>

        {/* Middle Section: Hardware Faceplate Viewer & Link Inspector */}
        <DeviceFaceplateViewer 
          device={device}
          cables={cables}
          allEquipment={rackEquipment}
          isLoading={loading}
          dcCode={dcCode || rack?.datacenter_code}
        />

        {/* Bottom Section: Ports Directory & Add Port */}
        <div className="bg-slate-900 border border-slate-800/50 rounded-xl p-5 shadow-sm flex flex-col gap-4">
          <div className="flex justify-between items-center border-b border-slate-800/20 pb-3">
            <h2 className="text-sm font-mono font-bold uppercase text-slate-100 flex items-center gap-2">
              <CableIcon className="w-4 h-4 text-cyan-400" /> Реестр Физических Портов
            </h2>
            <button 
              onClick={() => setShowAddPort(true)}
              className="flex items-center gap-1 px-3 py-1.5 bg-cyan-950/20 border border-cyan-800/50 text-cyan-400 rounded-lg hover:bg-cyan-900/30 transition-colors duration-200 text-xs font-mono focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
            >
              <Plus className="w-4 h-4" /> Добавить порт
            </button>
          </div>

          {device.ports && device.ports.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {device.ports.map((port: any) => {
                const portCable = cables.find((c: any) => c.port_a_id === port.id || c.port_b_id === port.id);
                const isConnected = !!portCable;

                return (
                  <div key={port.id} className="p-3 bg-slate-950/40 border border-slate-800/30 rounded-lg text-xs font-mono">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-2.5 h-2.5 rounded-full bg-slate-950 border border-slate-800 flex items-center justify-center">
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            isConnected ? 'bg-cyan-400 shadow-[0_0_4px_rgba(6,182,212,0.6)]' :
                            port.status === 'Connected' ? 'bg-cyan-400/50' :
                            port.status === 'Reserved'  ? 'bg-amber-400' : 'bg-slate-700'
                          }`} />
                        </div>
                        <div>
                          <p className="font-bold text-slate-100">{port.name}</p>
                          <p className="text-[10px] text-slate-400">{port.connector_type || 'RJ45'} • {port.port_type}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {isConnected ? (
                          <span className="px-2 py-0.5 bg-cyan-950/20 border border-cyan-200 dark:border-cyan-800/80 text-cyan-400 rounded text-[10px]">
                            Подключен (кабель)
                          </span>
                        ) : (
                          <select
                            value={port.status || 'Free'}
                            onChange={e => handlePortStatusChange(port.id, e.target.value)}
                            onClick={e => e.stopPropagation()}
                            className="px-1.5 py-0.5 bg-slate-900/60 border border-slate-800/50 text-slate-300 rounded text-[10px] cursor-pointer hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none transition-colors duration-200"
                            aria-label={`Статус порта ${port.name}`}
                          >
                            <option value="Free" className="bg-slate-900 text-slate-100">Свободен</option>
                            <option value="Reserved" className="bg-slate-900 text-slate-100">Зарезервирован</option>
                            <option value="Connected" className="bg-slate-900 text-slate-100">Подключен (вручную)</option>
                          </select>
                        )}
                        <button
                          onClick={() => handleDeletePort(port.id, port.name)}
                          className="p-1.5 text-slate-400 hover:text-rose-400 transition-colors duration-200 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
                          aria-label={`Удалить порт ${port.name}`}
                        >
                          <Trash2 className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </div>
                    </div>

                    {/* Context notes — visible when Reserved or manually Connected (no cable yet) */}
                    {!isConnected && (port.status === 'Reserved' || port.status === 'Connected') && (
                      <div className="mt-2 space-y-1">
                        <input
                          type="text"
                          placeholder={port.status === 'Connected' ? 'Куда подключён: устройство / порт (напр. Switch-01 / gi0/1)' : 'Причина резерва / описание...'}
                          value={portNotes[port.id] ?? (port.notes || '')}
                          onChange={e => setPortNotes(prev => ({ ...prev, [port.id]: e.target.value }))}
                          onBlur={() => handlePortNotesSave(port.id)}
                          className={`w-full rounded px-2 py-1 text-[10px] placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-accent transition-[border-color] duration-200 ${
                            port.status === 'Connected'
                              ? 'bg-slate-900/60 border border-cyan-800/40 text-cyan-400 focus:border-cyan-600'
                              : 'bg-slate-900/60 border border-amber-800/50 text-amber-500 focus:border-amber-600'
                          }`}
                        />
                        {port.status === 'Connected' && (
                          <p className="text-[9px] text-cyan-500/60">
                            ⚡ Трассировка недоступна. Добавьте кабель в «Коммутации» для полной интеграции.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs font-mono text-slate-400 text-center py-4">У данного прибора пока нет портов.</p>
          )}
        </div>

      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <DeviceEditModal
          device={device}
          datacenterCode={rack?.datacenter_code || ''}
          onClose={() => setShowEditModal(false)}
          onSuccess={fetchDeviceData}
        />
      )}

      {/* Add Port Modal */}
      {showAddPort && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-slate-900/90 border border-slate-800/50 rounded-xl shadow-2xl p-6 w-full max-w-sm backdrop-blur-md">
            <h3 className="text-base font-bold font-mono text-slate-100 mb-4">Добавление порта</h3>
            <form onSubmit={handleAddPort} className="space-y-4 text-xs font-mono">
              <div>
                <label htmlFor="port-add-name" className="block text-slate-400 mb-1">Имя порта</label>
                <input 
                  id="port-add-name"
                  name="portName"
                  type="text" 
                  value={newPort.name}
                  onChange={(e) => setNewPort({ ...newPort, name: e.target.value })}
                  placeholder="eth0, ge-0/0/1, PDU-1"
                  className="w-full bg-slate-900/60 border border-slate-800/50 rounded-lg p-2 text-slate-100 focus:outline-none focus:border-accent transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
                  required
                />
              </div>

              <div>
                <label htmlFor="port-add-connector" className="block text-slate-400 mb-1">Тип разъема</label>
                <select
                  id="port-add-connector"
                  name="connectorType"
                  value={newPort.connector_type}
                  onChange={(e) => setNewPort({ ...newPort, connector_type: e.target.value })}
                  className="w-full bg-slate-900/60 border border-slate-800/50 rounded-lg p-2 text-slate-100 focus:outline-none focus:border-accent transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none cursor-pointer"
                >
                  <optgroup label="Data (Copper)" className="bg-slate-900 text-slate-100">
                    <option value="RJ45">RJ45 — 1G/10G</option>
                    <option value="RJ11">RJ11 — Console</option>
                    <option value="USB-A">USB-A</option>
                    <option value="USB-C">USB-C</option>
                  </optgroup>
                  <optgroup label="Data (Fiber / SFP)" className="bg-slate-900 text-slate-100">
                    <option value="SFP">SFP — 1G</option>
                    <option value="SFP+">SFP+ — 10G</option>
                    <option value="SFP28">SFP28 — 25G</option>
                    <option value="QSFP+">QSFP+ — 40G</option>
                    <option value="QSFP28">QSFP28 — 100G</option>
                    <option value="QSFP56">QSFP56 — 200G</option>
                    <option value="QSFP-DD">QSFP-DD — 400G</option>
                    <option value="OSFP">OSFP — 400G</option>
                  </optgroup>
                  <optgroup label="Power" className="bg-slate-900 text-slate-100">
                    <option value="C13">C13 — PDU outlet</option>
                    <option value="C14">C14 — PDU inlet</option>
                    <option value="C19">C19 — PDU outlet (32A)</option>
                    <option value="C20">C20 — PDU inlet (32A)</option>
                    <option value="NEMA-5-15">NEMA 5-15</option>
                  </optgroup>
                  <optgroup label="Management" className="bg-slate-900 text-slate-100">
                    <option value="iDRAC">iDRAC (Dell)</option>
                    <option value="iLO">iLO (HPE)</option>
                    <option value="IPMI">IPMI</option>
                    <option value="CONSOLE-DB9">Console DB-9</option>
                    <option value="CONSOLE-RJ45">Console RJ-45</option>
                  </optgroup>
                </select>

              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button 
                  type="button" 
                  onClick={() => setShowAddPort(false)}
                  className="px-3 py-1.5 text-slate-400 hover:text-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none transition-colors duration-200"
                >
                  Отмена
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none transition-colors duration-200"
                >
                  Создать
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Swap Modal */}
      {showSwapModal && (
        <DeviceSwapModal
          device={device}
          datacenterCode={rack?.datacenter_code || 'DC1'}
          onClose={() => setShowSwapModal(false)}
        />
      )}

    </div>
  );
}
